import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Linkedin, Building2, Globe, MapPin, Lock, Check, X, LogIn, ExternalLink } from 'lucide-react';

const API_BASE  = '/api';
const AUTH_BASE = '/auth';

const SUPPORTED_LANGS = ['fr', 'en', 'de'];

const I18N = {
  fr: {
    back:                  'Retour',
    connect:               'Se connecter',
    pending:               'En attente',
    connected:             'Connecté·e',
    accept:                'Accepter',
    decline:               'Refuser',
    activities:            'Activités',
    categories:            'Catégories',
    looking_for:           'Ce qu\'ils recherchent',
    company_section:       'Entreprise',
    contact_locked:        'Identifiez-vous pour voir ses coordonnées',
    not_connected_hint:    'Les coordonnées sont visibles une fois identifié',
    login_title:           'Identification',
    login_subtitle:        'Entrez votre email pour vous connecter',
    email_label:           'Adresse email',
    email_placeholder:     'votre@email.com',
    send_code:             'Envoyer le code',
    code_label:            'Code de vérification',
    code_hint:             'Code à 6 chiffres reçu par email',
    verify:                'Vérifier',
    not_found:             "Compte non trouvé. Contactez l'organisateur.",
    back_btn:              'Retour',
    sending:               'Envoi…',
    verifying:             'Vérification…',
    login_to_connect:      'S\'identifier pour rentrer en contact',
    website:               'Site web',
    not_found_profile:     'Profil introuvable',
    loading:               'Chargement…',
    table_label:           'Table',
  },
  en: {
    back:                  'Back',
    connect:               'Connect',
    pending:               'Pending',
    connected:             'Connected',
    accept:                'Accept',
    decline:               'Decline',
    activities:            'Activities',
    categories:            'Categories',
    looking_for:           'What they\'re looking for',
    company_section:       'Company',
    contact_locked:        'Connect to see contact details',
    not_connected_hint:    'Contact details are visible once connected',
    login_title:           'Sign in',
    login_subtitle:        'Enter your email to connect',
    email_label:           'Email address',
    email_placeholder:     'your@email.com',
    send_code:             'Send code',
    code_label:            'Verification code',
    code_hint:             '6-digit code sent to your email',
    verify:                'Verify',
    not_found:             'Account not found. Contact the organizer.',
    back_btn:              'Back',
    sending:               'Sending…',
    verifying:             'Verifying…',
    login_to_connect:      'Sign in to connect',
    website:               'Website',
    not_found_profile:     'Profile not found',
    loading:               'Loading…',
    table_label:           'Table',
  },
  de: {
    back:                  'Zurück',
    connect:               'Verbinden',
    pending:               'Ausstehend',
    connected:             'Verbunden',
    accept:                'Annehmen',
    decline:               'Ablehnen',
    activities:            'Aktivitäten',
    categories:            'Kategorien',
    looking_for:           'Gesucht',
    company_section:       'Unternehmen',
    contact_locked:        'Verbinden, um Kontaktdaten zu sehen',
    not_connected_hint:    'Kontaktdaten sind nach Verbindung sichtbar',
    login_title:           'Anmeldung',
    login_subtitle:        'E-Mail eingeben um fortzufahren',
    email_label:           'E-Mail-Adresse',
    email_placeholder:     'ihre@email.com',
    send_code:             'Code senden',
    code_label:            'Bestätigungscode',
    code_hint:             '6-stelliger Code per E-Mail',
    verify:                'Bestätigen',
    not_found:             'Konto nicht gefunden. Veranstalter kontaktieren.',
    back_btn:              'Zurück',
    sending:               'Senden…',
    verifying:             'Überprüfen…',
    login_to_connect:      'Anmelden zum Verbinden',
    website:               'Website',
    not_found_profile:     'Profil nicht gefunden',
    loading:               'Laden…',
    table_label:           'Tisch',
  },
};

const getInitialLang = () => {
  const saved = localStorage.getItem('language');
  if (SUPPORTED_LANGS.includes(saved)) return saved;
  const browser = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(browser) ? browser : 'fr';
};

const MODAL_INIT = { open: false, step: 'email', email: '', code: '', loading: false, error: '', pendingAction: null };

const NetworkingProfilePage = () => {
  const { slug, recordId } = useParams();
  const navigate           = useNavigate();
  const lang               = getInitialLang();
  const i                  = I18N[lang] || I18N.fr;

  const [eventData, setEventData]       = useState(null);
  const [profile, setProfile]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [notFound, setNotFound]         = useState(false);
  const [networkingUser, setNetworkingUser] = useState(null);
  const [connectionState, setConnectionState] = useState(null); // null | 'pending_sent' | 'pending_received' | 'connected' | 'declined'
  const [connectionId, setConnectionId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [loginModal, setLoginModal]     = useState(MODAL_INIT);
  const [photoOpen, setPhotoOpen]       = useState(false);

  const subEventIdRef = useRef(null);

  // ── Load event + profile + auth ───────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      // Restore networking_user synchronously before any fetch
      let netUser = null;
      try {
        const net = localStorage.getItem('networking_user');
        if (net) {
          const parsed = JSON.parse(net);
          if (parsed?.record_id && parsed?.email) netUser = parsed;
        }
      } catch {}
      if (netUser) setNetworkingUser(netUser);

      // Use cached subevent_id (saved by participants page) to start profile fetch in parallel
      const cachedSubEventId = localStorage.getItem(`subevent_id_${slug}`);
      const profilePromise   = cachedSubEventId
        ? fetch(`${API_BASE}/networking-participant/${cachedSubEventId}/${recordId}`).then(r => r.json())
        : null;

      try {
        const evRes  = await fetch(`${API_BASE}/event/${slug}`);
        const evData = await evRes.json();
        if (!evData.success) throw new Error('Événement introuvable');

        const ev         = evData.event;
        const subEventId = ev.subevent_id || ev.id;
        setEventData(ev);
        subEventIdRef.current = subEventId;
        localStorage.setItem(`subevent_id_${slug}`, subEventId);

        // Use parallel profile fetch if subevent_id matches, otherwise fetch now
        let profData;
        if (profilePromise && cachedSubEventId === subEventId) {
          profData = await profilePromise;
        } else {
          const r = await fetch(`${API_BASE}/networking-participant/${subEventId}/${recordId}`);
          profData = await r.json();
        }
        if (!profData.success) { setNotFound(true); return; }
        setProfile(profData.data);

        // If no networking_user from localStorage, try dashboard session (needs subEventId)
        if (!netUser) await restoreAuth(subEventId);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, recordId]);

  // ── Load connection state when user identified ────────────────────────────
  useEffect(() => {
    if (!networkingUser?.record_id || !subEventIdRef.current || !recordId) return;
    if (networkingUser.record_id === recordId) return; // viewing own profile

    fetch(`${API_BASE}/networking-connections/${subEventIdRef.current}?me=${networkingUser.record_id}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        const conn = d.data.find(c =>
          (c.sender_id === networkingUser.record_id && c.receiver_id === recordId) ||
          (c.receiver_id === networkingUser.record_id && c.sender_id === recordId)
        );
        if (!conn) { setConnectionState(null); return; }
        setConnectionId(conn.id);
        if (conn.status === 'on') setConnectionState('connected');
        else if (conn.status === 'decline') setConnectionState('declined');
        else if (conn.sender_id === networkingUser.record_id) setConnectionState('pending_sent');
        else setConnectionState('pending_received');
      })
      .catch(() => {});
  }, [networkingUser, recordId]);

  // ── Auth restore (dashboard session fallback) ─────────────────────────────
  const restoreAuth = async (subEventId) => {
    try {
      const token   = localStorage.getItem('user_token');
      const raw     = localStorage.getItem('user_data');
      const expires = localStorage.getItem('session_expires');
      if (token && raw && expires && new Date(expires) > new Date()) {
        const parsed = JSON.parse(raw);
        if (parsed.email) {
          const res  = await fetch(`${API_BASE}/profile?email=${encodeURIComponent(parsed.email)}&subevent=${subEventId}`);
          const data = await res.json();
          if (data.success && data.profile?.record_id) {
            const u = { email: parsed.email, record_id: data.profile.record_id };
            setNetworkingUser(u);
            localStorage.setItem('networking_user', JSON.stringify(u));
          }
        }
      }
    } catch {}
  };

  // ── Connect ───────────────────────────────────────────────────────────────
  const doConnect = async () => {
    if (!networkingUser?.record_id) return;
    setActionLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/networking-connect`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ sender_id: networkingUser.record_id, receiver_id: recordId, subevent_id: subEventIdRef.current }),
      });
      const data = await res.json();
      if (data.success) {
        setConnectionState('pending_sent');
        setConnectionId(data.connection_id);
      }
    } catch {}
    setActionLoading(false);
  };

  const handleConnect = () => {
    if (!networkingUser) {
      setLoginModal({ ...MODAL_INIT, open: true, pendingAction: 'connect' });
    } else {
      doConnect();
    }
  };

  // ── Respond ───────────────────────────────────────────────────────────────
  const handleRespond = async (status) => {
    if (!connectionId) return;
    setActionLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/networking-respond/${connectionId}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.success) setConnectionState(status === 'on' ? 'connected' : 'declined');
    } catch {}
    setActionLoading(false);
  };

  // ── Login modal ───────────────────────────────────────────────────────────
  const handleLoginEmail = async () => {
    if (!loginModal.email) return;
    setLoginModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const res  = await fetch(`${AUTH_BASE}/send_networking_code.php`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          email:      loginModal.email,
          event_id:   subEventIdRef.current,
          event_name: eventData?.name || '',
          color_main: eventData?.color_main || '#1c1b3a',
        }),
      });
      const data = await res.json();
      if (data.ok) setLoginModal(prev => ({ ...prev, loading: false, step: 'code' }));
      else setLoginModal(prev => ({ ...prev, loading: false, error: data.not_found ? i.not_found : (data.error || i.not_found) }));
    } catch {
      setLoginModal(prev => ({ ...prev, loading: false, error: i.not_found }));
    }
  };

  const handleLoginCode = async () => {
    if (loginModal.code.length !== 6) return;
    setLoginModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const verRes  = await fetch(`${AUTH_BASE}/verify_code.php`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: loginModal.email, event_id: subEventIdRef.current, code: loginModal.code }),
      });
      const verData = await verRes.json();
      if (!verData.ok) {
        setLoginModal(prev => ({ ...prev, loading: false, error: verData.error || 'Code invalide' }));
        return;
      }
      const profRes  = await fetch(`${API_BASE}/profile?email=${encodeURIComponent(loginModal.email)}&subevent=${subEventIdRef.current}`);
      const profData = await profRes.json();
      if (!profData.success || !profData.profile?.record_id) {
        setLoginModal(prev => ({ ...prev, loading: false, error: i.not_found }));
        return;
      }
      const u = { email: loginModal.email, record_id: profData.profile.record_id };
      setNetworkingUser(u);
      localStorage.setItem('networking_user', JSON.stringify(u));
      const action = loginModal.pendingAction;
      setLoginModal(MODAL_INIT);
      if (action === 'connect') setTimeout(doConnect, 100);
    } catch {
      setLoginModal(prev => ({ ...prev, loading: false, error: 'Erreur de vérification' }));
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const isConnected       = connectionState === 'connected';
  const isPendingReceived = connectionState === 'pending_received';
  const isPendingSent     = connectionState === 'pending_sent';
  const isOwnProfile      = networkingUser?.record_id === recordId;

  const renderConnectionButton = () => {
    if (isOwnProfile) return null;

    if (!networkingUser) {
      return (
        <button onClick={handleConnect} className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition flex items-center justify-center gap-2">
          <LogIn size={16} />
          {i.login_to_connect}
        </button>
      );
    }

    if (!connectionState) {
      return (
        <button onClick={handleConnect} disabled={actionLoading} className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition disabled:opacity-50">
          {actionLoading ? '…' : i.connect}
        </button>
      );
    }

    if (isPendingReceived) {
      return (
        <div className="flex gap-2">
          <button onClick={() => handleRespond('on')} disabled={actionLoading} className="flex-1 py-3 rounded-xl bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition disabled:opacity-50">
            {actionLoading ? '…' : i.accept}
          </button>
          <button onClick={() => handleRespond('decline')} disabled={actionLoading} className="flex-1 py-3 rounded-xl bg-red-100 text-red-600 font-semibold text-sm hover:bg-red-200 transition disabled:opacity-50">
            {i.decline}
          </button>
        </div>
      );
    }

    if (isPendingSent) {
      return (
        <div className="w-full py-3 rounded-xl bg-orange-50 text-orange-600 font-semibold text-sm text-center border border-orange-200">
          {i.pending}
        </div>
      );
    }

    if (isConnected) {
      return (
        <div className="w-full py-3 rounded-xl bg-green-50 text-green-700 font-semibold text-sm text-center border border-green-200 flex items-center justify-center gap-2">
          <Check size={16} />
          {i.connected}
        </div>
      );
    }

    return null;
  };

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <p className="text-gray-500 mb-4">{i.not_found_profile}</p>
        <button onClick={() => navigate(-1)} className="text-blue-600 font-semibold text-sm flex items-center gap-1">
          <ArrowLeft size={16} /> {i.back}
        </button>
      </div>
    );
  }

  const p = profile;

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Header ── */}
      <div className="relative bg-gradient-to-br from-blue-700 to-blue-900 px-4 pt-4 pb-8">
        {/* Back button */}
        <button
          onClick={() => navigate(`/event/${slug}/participants-networking`)}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm mb-4 transition"
        >
          <ArrowLeft size={16} />
          {i.back}
        </button>

        {/* Profile header — centered */}
        <div className="flex flex-col items-center text-center gap-2">
          {p.photo ? (
            <button onClick={() => setPhotoOpen(true)} className="focus:outline-none">
              <img src={p.photo} alt={p.firstname} className="w-16 h-16 rounded-full object-cover border-2 border-white/30 shadow-lg cursor-pointer hover:opacity-90 transition" />
            </button>
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold shadow-lg select-none border border-white/20">
              {(p.lastname?.[0] || '') + (p.firstname?.[0] || '')}
            </div>
          )}
          <div>
            <h1 className="text-white font-bold text-lg leading-snug">
              <span className="uppercase">{p.lastname}</span>{' '}
              <span className="font-light">{p.firstname}</span>
            </h1>
            {p.fonction && <p className="text-blue-200 text-sm mt-0.5">{p.fonction}</p>}
            {p.company && (
              <span className="inline-flex items-center gap-1 mt-2 bg-white/15 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                <Building2 size={10} className="opacity-70" />
                {p.company}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-2xl mx-auto px-4 mt-6 pb-10 space-y-3">

        {/* Connect card */}
        {!isOwnProfile && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            {renderConnectionButton()}
          </div>
        )}

        {/* Contact info — visible only if connected */}
        {isConnected && (p.email || p.phone || p.linkedin) ? (
          <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
            {p.email && (
              <a href={`mailto:${p.email}`} className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition">
                  <Mail size={16} className="text-blue-500" />
                </div>
                <span className="text-sm text-gray-700 group-hover:text-blue-600 truncate">{p.email}</span>
              </a>
            )}
            {p.phone && (
              <a href={`tel:${p.phone}`} className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition">
                  <Phone size={16} className="text-green-500" />
                </div>
                <span className="text-sm text-gray-700 group-hover:text-green-600">{p.phone}</span>
              </a>
            )}
            {p.linkedin && (
              <a href={p.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition">
                  <Linkedin size={16} className="text-blue-600" />
                </div>
                <span className="text-sm text-blue-600 group-hover:underline">LinkedIn</span>
              </a>
            )}
          </div>
        ) : !isOwnProfile && !isConnected && (p.email || p.phone || p.linkedin) ? (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Lock size={16} className="text-gray-400" />
              </div>
              <p className="text-sm">{i.not_connected_hint}</p>
            </div>
          </div>
        ) : null}

        {/* Description / bio */}
        {p.description && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{p.description}</p>
          </div>
        )}

        {/* Categories */}
        {p.category?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{i.categories}</p>
            <div className="flex flex-wrap gap-1.5">
              {p.category.map((c, idx) => (
                <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full font-medium">{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* Company requests */}
        {p.company_requests?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{i.looking_for}</p>
            <div className="flex flex-wrap gap-1.5">
              {p.company_requests.map((r, idx) => (
                <span key={idx} className="text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full font-medium border border-amber-100">{r}</span>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        {p.table && (
          <div className="bg-white rounded-2xl shadow-sm px-4 py-3 flex items-center gap-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{i.table_label}</span>
            <span className="text-sm font-semibold text-gray-800">{p.table}</span>
          </div>
        )}

        {/* Company */}
        {(p.company || p.company_description || p.company_type || p.company_activities?.length > 0 || p.company_website) && (
          <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{i.company_section}</p>

            {/* Company header */}
            <div className="flex items-center gap-3">
              {p.company_logo ? (
                <img src={p.company_logo} alt={p.company} className="w-12 h-12 rounded-xl object-contain border border-gray-100 bg-white p-1 flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Building2 size={20} className="text-gray-400" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 text-sm">{p.company}</p>
                {p.company_type && (
                  <span className="inline-block text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium mt-0.5">
                    {p.company_type}
                  </span>
                )}
              </div>
              {p.company_website && (
                <a
                  href={p.company_website.startsWith('http') ? p.company_website : `https://${p.company_website}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 transition"
                >
                  <Globe size={14} />
                  <span className="hidden sm:inline">{i.website}</span>
                </a>
              )}
            </div>

            {p.company_description && (
              <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl px-3 py-2.5 whitespace-pre-wrap">
                {p.company_description}
              </p>
            )}

            {p.company_activities?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {p.company_activities.map((a, idx) => (
                  <span key={idx} className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium">{a}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Photo Lightbox */}
      {photoOpen && p.photo && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPhotoOpen(false)}
        >
          <button
            onClick={() => setPhotoOpen(false)}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition z-10"
          >
            <X size={28} />
          </button>
          <img
            src={p.photo}
            alt={p.firstname}
            className="max-w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Login Modal */}
      {loginModal.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Drag handle — mobile only */}
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="px-6 pt-4 pb-8 sm:p-6" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-gray-900">{i.login_title}</h2>
              <button onClick={() => setLoginModal(MODAL_INIT)} className="text-gray-400 hover:text-gray-600 p-1">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">{i.login_subtitle}</p>

            {loginModal.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{loginModal.error}</div>
            )}

            {loginModal.step === 'email' ? (
              <>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{i.email_label}</label>
                <div className="relative mb-4">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={loginModal.email}
                    onChange={e => setLoginModal(prev => ({ ...prev, email: e.target.value, error: '' }))}
                    onKeyDown={e => e.key === 'Enter' && handleLoginEmail()}
                    placeholder={i.email_placeholder}
                    className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleLoginEmail}
                  disabled={!loginModal.email || loginModal.loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loginModal.loading ? i.sending : i.send_code}
                </button>
              </>
            ) : (
              <>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{i.code_label}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={loginModal.code}
                  onChange={e => setLoginModal(prev => ({ ...prev, code: e.target.value.replace(/\D/g, ''), error: '' }))}
                  onKeyDown={e => e.key === 'Enter' && handleLoginCode()}
                  placeholder="123456"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-center text-2xl tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                  autoFocus
                />
                <p className="text-xs text-gray-400 text-center mb-4">{i.code_hint}</p>
                <button
                  onClick={handleLoginCode}
                  disabled={loginModal.code.length !== 6 || loginModal.loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loginModal.loading ? i.verifying : i.verify}
                </button>
                <button
                  onClick={() => setLoginModal(prev => ({ ...prev, step: 'email', code: '', error: '' }))}
                  className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  {i.back_btn}
                </button>
              </>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkingProfilePage;
